/**
 * Tiny fetch wrapper that adds Bearer auth + JSON parsing.
 * Reads token from sessionStorage; AuthContext is the only writer.
 */
// Hostname-based env routing — Vite only distinguishes dev/prod, but we run
// two separate prod-mode bundles (prod + staging.home.sigmacog.xyz). Without
// this, staging.home.sigmacog.xyz hit the prod Worker, which generated prod
// join URLs and dragged the case onto the prod stack mid-test.
function resolveApiBase(): string {
  if (typeof window === "undefined") return "https://cognihome-api.swlucifer.workers.dev";
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1") return "http://localhost:8787";
  if (h.startsWith("staging.")) return "https://cognihome-api-staging.swlucifer.workers.dev";
  return "https://cognihome-api.swlucifer.workers.dev";
}
const BASE = resolveApiBase();

export interface ApiError { error: string; message?: string; hint?: string }
export class ApiException extends Error {
  status: number;
  body: ApiError;
  constructor(status: number, body: ApiError) {
    super(body.error || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

function token(): string | null {
  return sessionStorage.getItem("cognihome_access_token");
}
function refreshToken(): string | null {
  return sessionStorage.getItem("cognihome_refresh_token");
}

interface Options {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  headers?: Record<string, string>;
  noAuth?: boolean;
}

/**
 * Exchange the stored refresh_token for a new access_token + refresh_token.
 * Updates sessionStorage on success, clears on hard failure (401/403 from
 * /auth/refresh — the refresh itself is invalid, force re-login).
 *
 * Returns the new access token, or null if refresh failed. Concurrent
 * callers share a single in-flight refresh promise so a burst of 401s
 * doesn't fan out N refresh requests.
 */
let refreshInFlight: Promise<string | null> | null = null;
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const rt = refreshToken();
    if (!rt) return null;
    try {
      const r = await fetch(`${BASE}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      const body = await r.json().catch(() => null) as
        | { ok: true; access_token: string; refresh_token: string }
        | { ok: false; error: string }
        | null;
      if (!r.ok || !body || body.ok !== true) {
        // Hard auth failure — clear tokens so the AuthProvider sees a logged-out state
        if (r.status === 401 || r.status === 403) {
          sessionStorage.removeItem("cognihome_access_token");
          sessionStorage.removeItem("cognihome_refresh_token");
          // Notify other tabs / components that auth ended.
          window.dispatchEvent(new CustomEvent("cognihome:auth-cleared"));
        }
        return null;
      }
      sessionStorage.setItem("cognihome_access_token",  body.access_token);
      sessionStorage.setItem("cognihome_refresh_token", body.refresh_token);
      return body.access_token;
    } catch {
      return null;
    }
  })().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

async function doFetch(path: string, opts: Options, t: string | null): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers: {
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      ...opts.headers,
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const t0 = opts.noAuth ? null : token();
  let r = await doFetch(path, opts, t0);
  // P7 hardening: on 401 with an access token, try refresh-once + retry.
  // Avoids the user being silently kicked at the 2 h access-token expiry
  // mid-session. Skipped for explicit noAuth requests + the refresh
  // endpoint itself.
  if (r.status === 401 && !opts.noAuth && t0 && !path.startsWith("/api/auth/refresh")) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      r = await doFetch(path, opts, newToken);
    }
  }
  let body: any = null;
  try { body = await r.json(); } catch { /* */ }
  if (!r.ok || (body && body.ok === false)) {
    throw new ApiException(r.status, body ?? { error: "network_error" });
  }
  return body as T;
}

export const API_BASE = BASE;
